import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import qapi from "views_shared/app/javascript/api";
import TranscriptPanel from "./TranscriptPanel";

// Presentation player for a resolved video attachment, shared by every surface that shows a Qwoted
// video: the messaging thread (frontend my_inbox/Attachment), the feed card and the admin review
// queue (both via FeedVideoPlayer). Each consumer owns the initial fetch of the attachment (the
// inbox already fetches to dispatch on type; the feed/admin wrapper fetches the signed URL for a
// known-video mat reference) and hands the resolved `display` in; this component owns everything
// downstream — poster→tap activation, the iOS imperative-player workaround, playback self-heal,
// transcript polling, and timestamp seeking.
//
// It calls AttachmentsController#show (via qapi.attachments.find) only to self-heal an expired or
// not-yet-ready signed URL, and the lean transcript_status endpoint to poll transcription progress —
// both keyed on `attachmentId`, never on mount (the mount fetch is the consumer's).

// iOS WebKit wedges React-reconciled players (loader stalls at t=0 with a full buffer, no error)
// while an identical imperatively-created element plays — verified per-variant on device. On iOS the
// activated player is therefore built outside React's reconciler.
const IS_IOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

// Cloudinary builds the transformed MP4 derivative lazily on first request, so a just-created video
// can 404 for a second or two until it's ready. Retry the player a few times before giving up.
const MAX_VIDEO_RETRIES = 5;
const VIDEO_RETRY_MS = 2000;

// Transcription runs in the background after upload and can take minutes (Sidekiq queue + Cloudinary
// building the mp3 derivative + Whisper + retries). Poll with a backoff from 3s toward 30s so the
// toggle still appears for slow runs (~5 min budget) without a burst of early requests.
const MAX_TRANSCRIPT_POLLS = 15;
const TRANSCRIPT_POLL_BASE_MS = 3000;
const TRANSCRIPT_POLL_MAX_MS = 30000;
const transcriptPollDelay = (attempt) => Math.min(TRANSCRIPT_POLL_BASE_MS * (attempt + 1), TRANSCRIPT_POLL_MAX_MS);

function VideoPlayer({ attachmentId, display = null, maxPreviewPx }) {
  // Seed the render data from the resolved attachment the consumer fetched; self-heal and transcript
  // polls update this internal copy without another mount fetch. Re-sync if the consumer swaps it.
  const [serverAttachment, setServerAttachment] = useState(display);
  const [videoRetry, setVideoRetry] = useState(0);
  const [transcriptPoll, setTranscriptPoll] = useState(0);
  // Videos render as a poster until tapped: N mounted preload="metadata" players wedge on iOS (loader
  // suspends after metadata and never wakes: play -> waiting@0 forever) and pile up against WebKit's
  // decoder limits. Mount-on-tap with preload="auto" sidesteps both.
  const [videoActivated, setVideoActivated] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const pendingSeekRef = useRef(null);
  const videoRetryTimer = useRef(null);
  const transcriptPollTimer = useRef(null);
  const videoRef = useRef(null);
  // Assumes a stable attachmentId per instance (every consumer renders one player per attachment and
  // keys by id) — never reset, like videoActivated. If a player were ever reused for a different
  // attachment without remounting, reset this (and videoActivated) on an attachmentId change.
  const viewTrackedRef = useRef(false);

  useEffect(() => setServerAttachment(display), [display]);

  useEffect(() => () => clearTimeout(videoRetryTimer.current), []);

  // Poll while transcription is pending/processing so the toggle appears without a reload.
  const transcriptStatus = serverAttachment?.transcript?.status;
  useEffect(() => {
    const waiting = transcriptStatus === "pending" || transcriptStatus === "processing";
    if (!waiting || transcriptPoll >= MAX_TRANSCRIPT_POLLS) {
      return undefined;
    }

    transcriptPollTimer.current = setTimeout(() => {
      setTranscriptPoll((n) => n + 1);
      // Lean status endpoint: no signed-URL regen, no usage tracking. Merge only the transcript so
      // the <video> src stays put (swapping it would reload the player and drop an in-flight seek).
      qapi.attachments.transcriptStatus(attachmentId).then(({ data }) => {
        if (data) {
          setServerAttachment((prev) => (prev ? { ...prev, transcript: data.transcript } : prev));
        }
      });
    }, transcriptPollDelay(transcriptPoll));
    return () => clearTimeout(transcriptPollTimer.current);
  }, [transcriptStatus, transcriptPoll, attachmentId]);

  // Playback can fail for two reasons: the transformed MP4 derivative isn't built yet (404 on a
  // just-created clip), or the signed URL's short-lived token has expired (page left open a while).
  // Both recover the same way — re-fetch a fresh signed URL and re-mount the player — so an expired
  // link self-heals invisibly rather than showing a broken video. Bounded so a genuinely dead asset
  // doesn't loop forever.
  const handleVideoError = () => {
    if (videoRetry >= MAX_VIDEO_RETRIES) {
      return;
    }
    // Clear any pending retry so a burst of error events can't schedule overlapping timers (each of
    // which would fire its own re-fetch and burn the retry budget faster than intended).
    clearTimeout(videoRetryTimer.current);
    videoRetryTimer.current = setTimeout(() => {
      qapi.attachments.find(attachmentId).then(({ data }) => {
        if (data) {
          setServerAttachment(data);
        }
        setVideoRetry((n) => n + 1);
      });
    }, VIDEO_RETRY_MS);
  };

  // Deref at click time: the <video> remounts on retry (key={videoRetry}) and the ref re-binds.
  const handleSeek = (start) => {
    if (!videoActivated) {
      pendingSeekRef.current = start;
      setVideoActivated(true);
      return;
    }
    const node = videoRef.current;
    if (!node) {
      return;
    }

    const play = () => node.play()?.catch(() => {}); // play() -> promise in browsers, undefined under jsdom
    const canSeekTo = (time) => node.seekable.length > 0 && time <= node.seekable.end(node.seekable.length - 1);

    if (canSeekTo(start)) {
      node.currentTime = start;
      play();
      return;
    }

    // Fresh Cloudinary derivatives are served without range support (seekable=[0,0]), so forward
    // seeks fail; reload for a range-capable response, then seek on canplay (same as a page reload).
    const seekOnceReady = () => {
      node.removeEventListener("canplay", seekOnceReady);
      node.currentTime = start;
      play();
    };
    node.addEventListener("canplay", seekOnceReady);
    node.load();
  };

  // Record the engagement "view" once, when the user first plays the video — not on mount (the
  // poster/URL fetch tracks no video view) and not again on a self-heal remount. This is the tracked
  // signal for "watched", distinct from "rendered".
  useEffect(() => {
    if (!videoActivated || viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    qapi.attachments.trackView(attachmentId).catch(() => {});
  }, [videoActivated, attachmentId]);

  // Run a transcript-initiated seek once the tapped player has mounted.
  useEffect(() => {
    if (!videoActivated || pendingSeekRef.current == null) return;
    const start = pendingSeekRef.current;
    pendingSeekRef.current = null;
    const node = videoRef.current;
    if (!node) return;
    const seekOnceReady = () => {
      node.removeEventListener("canplay", seekOnceReady);
      node.currentTime = start;
      node.play()?.catch(() => {});
    };
    node.addEventListener("canplay", seekOnceReady);
  }, [videoActivated]);

  const resolved = serverAttachment;
  if (!resolved || !resolved.video) return null;

  // Cache-bust per retry so the browser re-requests instead of replaying the cached 404; `key`
  // forces the element to remount and reload the new src.
  const videoSrc =
    videoRetry === 0
      ? resolved.private_url
      : `${resolved.private_url}${resolved.private_url.includes("?") ? "&" : "?"}_r=${videoRetry}`;

  const transcriptReady = resolved.transcript?.status === "ready" && resolved.transcript.segments?.length > 0;

  return (
    <div className="mt-2" style={{ width: "fit-content", maxWidth: "100%" }} data-testid="video-player">
      {/* Panel is a sibling of this wrapper so it can fan out without stretching the meta row. */}
      <div className="transcript-media">
        {videoActivated && IS_IOS ? (
          <div
            // key on videoRetry so a self-heal re-fetch remounts the host and rebuilds the element
            // with the fresh URL — the dataset.mounted guard would otherwise pin the stale src.
            key={videoRetry}
            ref={(host) => {
              if (!host || host.dataset.mounted) return;
              // eslint-disable-next-line no-param-reassign -- marking the host node is the point
              host.dataset.mounted = "1";
              const el = document.createElement("video");
              el.src = videoSrc;
              el.controls = true;
              el.setAttribute("playsinline", "");
              // Same self-heal as the non-iOS player: on a 404 (derivative not ready) or an expired
              // signed URL, re-fetch a fresh URL and remount rather than showing a broken video.
              el.onerror = handleVideoError;
              el.className = "rounded border d-block";
              el.style.maxHeight = `${maxPreviewPx}px`;
              el.style.maxWidth = "100%";
              el.style.background = "#000";
              videoRef.current = el;
              host.appendChild(el);
            }}
          />
        ) : null}
        {videoActivated && !IS_IOS ? (
          <video
            key={videoRetry}
            ref={videoRef}
            src={videoSrc}
            onError={handleVideoError}
            controls
            autoPlay
            playsInline
            preload="auto"
            className="rounded border"
            style={{ maxHeight: maxPreviewPx, maxWidth: "100%", display: "block", background: "#000" }}
          >
            <track kind="captions" />
          </video>
        ) : null}
        {!videoActivated ? (
          <button
            type="button"
            className="position-relative border-0 p-0 rounded d-block bg-transparent"
            onClick={() => setVideoActivated(true)}
            aria-label={`Play ${resolved.filename}`}
          >
            {resolved.poster_url ? (
              <img
                src={resolved.poster_url}
                alt={resolved.filename}
                className="rounded border d-block"
                style={{ maxHeight: maxPreviewPx, maxWidth: "100%" }}
              />
            ) : (
              <span className="rounded border d-block bg-dark" style={{ width: 280, aspectRatio: "16 / 9" }} />
            )}
            <span
              className="position-absolute top-50 start-50 translate-middle rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: 52, height: 52, background: "rgba(255,255,255,.9)" }}
            >
              <i className="fa-solid fa-play text-dark" aria-hidden="true" />
            </span>
          </button>
        ) : null}
        {resolved.recorded ? (
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-md-between gap-2 gap-md-3 mt-2">
            <div className="d-flex align-items-center gap-1">
              <i
                className="fa-solid fa-circle-check text-accent-blue-60"
                aria-hidden="true"
                style={{ marginTop: "2px" }}
              />
              <span className="text-neutral-100">Recorded live on Qwoted</span>
            </div>
            {transcriptReady && (
              <div className="transcript-toggle form-check form-switch d-flex align-items-center gap-2 m-0 ps-0">
                <label className="form-check-label text-neutral-100" htmlFor={`transcript-toggle-${attachmentId}`}>
                  View Transcript
                </label>
                <input
                  id={`transcript-toggle-${attachmentId}`}
                  className="form-check-input ms-0 mt-0 float-none"
                  type="checkbox"
                  role="switch"
                  checked={showTranscript}
                  onChange={() => setShowTranscript((visible) => !visible)}
                />
              </div>
            )}
          </div>
        ) : (
          <small className="text-muted text-truncate d-block mt-1">{resolved.filename}</small>
        )}
      </div>
      {resolved.recorded && showTranscript && transcriptReady && (
        <TranscriptPanel segments={resolved.transcript.segments} onSeek={handleSeek} />
      )}
    </div>
  );
}

VideoPlayer.propTypes = {
  attachmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  // The resolved attachment the consumer fetched: { video, private_url, poster_url, recorded,
  // filename, transcript }. Presentation-only — this component never mounts-fetches it.
  // INVARIANT: pass a referentially-stable object — a consumer's useState/memoised value, not a
  // fresh literal every render. Self-heal and transcript polls accumulate on an internal copy of
  // this; a new `display` identity re-seeds that copy (needed so an id/attachment swap re-syncs),
  // which would discard in-flight self-heal/poll progress if the identity churned every render.
  display: PropTypes.shape({
    video: PropTypes.bool,
    filename: PropTypes.string,
    recorded: PropTypes.bool,
    poster_url: PropTypes.string,
    private_url: PropTypes.string,
    transcript: PropTypes.shape({
      status: PropTypes.string,
      segments: PropTypes.arrayOf(PropTypes.shape({ start: PropTypes.number, text: PropTypes.string })),
    }),
  }),
  maxPreviewPx: PropTypes.number.isRequired,
};

export default VideoPlayer;
